/* eslint-disable valid-jsdoc */
/**
 * WebSocket 客户端配置选项
 */
export interface WebSocketClientOptions {
  url: string; // WebSocket 服务器地址，例如 'ws://localhost:3333'
  autoReconnect?: boolean; // 是否自动重连，默认为 true
  reconnectInterval?: number; // 重连间隔（毫秒），默认 3000ms
  heartbeatInterval?: number; // 心跳发送间隔（毫秒），应小于服务端超时时间，默认 25000ms
  heartbeatMessage?: string; // 发送的心跳消息内容，默认 '__heartbeat__'
}

/*发送出去的数据格式*/
export interface SendData {
  type: "reg" | "msg"; //转发形式 reg | msg
  toClientIp: string; //转发目标客户端 all代表广播
  forwardingType: "txt" | "hex"; //转发形式 str | hex
  forwardingChannels: "ws" | "tcp" | "udp"; //转发渠道 ws | tcp | udp
  message: string; //消息内容
}

/**
 * WebSocket 客户端事件类型
 */
export type WebSocketEvent =
  | "open"
  | "close"
  | "message"
  | "error"
  | "reconnect";

/**
 * WebSocket 客户端类（TypeScript 版，支持心跳、自动重连、事件驱动）
 */
export class WebSocketClient {
  // ========== 配置属性（来自构造参数） ==========
  private url: string; // WebSocket 服务端地址
  private autoReconnect: boolean; // 是否启用自动重连（主动断开时会临时禁用）
  private reconnectInterval: number; // 重连尝试的间隔时间（毫秒）
  private heartbeatIntervalMs: number; // 心跳包的发送间隔（毫秒）
  private heartbeatMessage: string; // 心跳包的内容（字符串）

  // ========== 内部状态属性 ==========
  private ws: WebSocket | null = null; // 原生 WebSocket 实例
  private connected = false; // 当前是否为已连接状态
  private heartbeatTimer: number | null = null; // 心跳定时器 ID
  private reconnectTimer: number | null = null; // 重连定时器 ID
  private manualClose = false; // 是否由用户主动断开（用于抑制自动重连）

  // ========== 事件监听器存储 ==========
  private eventHandlers: Record<
    WebSocketEvent,
    Array<(...args: any[]) => void>
  > = {
    open: [], // 连接成功时的回调函数列表
    close: [], // 连接关闭时的回调函数列表
    message: [], // 收到消息时的回调函数列表
    error: [], // 发生错误时的回调函数列表
    reconnect: [], // 开始重连时的回调函数列表
  };

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.autoReconnect = options.autoReconnect !== false;
    this.reconnectInterval = options.reconnectInterval ?? 3000;
    this.heartbeatIntervalMs = options.heartbeatInterval ?? 25000;
    this.heartbeatMessage = options.heartbeatMessage ?? "__heartbeat__";
  }

  // ---------- 事件订阅 ----------
  /**
   * 注册事件监听
   * @param event - 事件名 ('open', 'close', 'message', 'error', 'reconnect')
   * @param callback - 回调函数
   */
  public on(event: WebSocketEvent, callback: (...args: any[]) => void): void {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].push(callback);
    }
  }

  /**
   * 移除事件监听
   * @param event - 事件名
   * @param callback - 要移除的回调函数
   */
  public off(event: WebSocketEvent, callback: (...args: any[]) => void): void {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event] = this.eventHandlers[event].filter(
        (cb) => cb !== callback
      );
    }
  }

  /**
   * 触发事件（内部使用）
   * @param event - 事件名
   * @param args - 传递给回调函数的参数
   */
  private emit(event: WebSocketEvent, ...args: any[]): void {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].forEach((cb) => cb(...args));
    }
  }

  // ---------- 内部方法（心跳与重连） ----------
  /** 清除心跳定时器 */
  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /** 启动心跳定时器，按配置间隔发送心跳消息 */
  private startHeartbeat(): void {
    this.clearHeartbeat();
    if (!this.connected) {
      return;
    }
    this.heartbeatTimer = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // console.log(this.ws.readyState, WebSocket.OPEN)
        this.ws.send(this.heartbeatMessage);
      }
    }, this.heartbeatIntervalMs);
  }

  /** 清除重连定时器 */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** 尝试重连（内部调用） */
  private attemptReconnect(): void {
    if (!this.autoReconnect || this.manualClose) {
      return;
    }
    this.clearReconnectTimer();
    this.reconnectTimer = window.setTimeout(() => {
      // console.log("重新连接")
      this.emit("reconnect"); // 触发重连事件，外部可做 UI 提示
      this.connect(); // 重新连接
    }, this.reconnectInterval);
  }

  // ---------- 公共 API ----------
  /**
   * 建立 WebSocket 连接
   * @returns Promise，成功时 resolve，失败时 reject
   */
  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }
      if (this.ws) {
        this.ws.close();
      }

      this.manualClose = false;
      try {
        this.ws = new WebSocket(this.url);

        this.ws.onopen = (event) => {
          this.connected = true;
          this.clearReconnectTimer();
          this.startHeartbeat();
          this.emit("open", event);
          resolve();
        };

        this.ws.onmessage = (event) => {
          const data = event.data; // 类型为 any（实际可能是 string / Blob / ArrayBuffer）
          if (typeof data === "string") {
            // 文本消息直接触发
            this.emit("message", data);
          } else if (data instanceof Blob) {
            // 二进制 Blob 消息，转为文本后触发
            const reader = new FileReader();
            reader.onload = () => {
              this.emit("message", reader.result as string);
            };
            reader.readAsText(data);
          } else {
            // 其他类型（如 ArrayBuffer）可按需处理，这里直接传递
            this.emit("message", data);
          }
        };

        this.ws.onerror = (error) => {
          this.emit("error", error);
          reject(error);
        };

        this.ws.onclose = (event) => {
          this.connected = false;
          this.clearHeartbeat();
          this.emit("close", event);
          if (!this.manualClose) {
            console.log("重新连接～");
            this.attemptReconnect(); // 非主动断开则自动重连
          }
        };
      } catch (err) {
        this.emit("error", err as Error);
        reject(err);
        this.attemptReconnect();
      }
    });
  }

  /**
   * 主动断开连接
   * @param disableAutoReconnect - 是否临时禁用自动重连（默认 true）
   */
  public disconnect(disableAutoReconnect = true): void {
    this.manualClose = disableAutoReconnect;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
    this.clearHeartbeat();
    this.clearReconnectTimer();
  }

  /**
   * 发送消息
   * @param message - 消息内容（字符串或二进制 ArrayBuffer）
   * @param hexMode - 是否为十六进制模式（仅对字符串有效，将十六进制文本转为二进制发送）
   * @returns 是否发送成功
   */
  public send(message: string | ArrayBuffer, hexMode = false): boolean {
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.emit("error", new Error("WebSocket 未连接，无法发送消息"));
      return false;
    }

    try {
      let data: string | ArrayBuffer = message;
      if (hexMode && typeof message === "string") {
        // 将十六进制字符串（如 "48656c6c6f"）转换为 ArrayBuffer
        const hexStr = message.replace(/\s/g, "");
        const bytes = new Uint8Array(hexStr.length / 2);
        for (let i = 0; i < hexStr.length; i += 2) {
          bytes[i / 2] = parseInt(hexStr.substr(i, 2), 16);
        }
        data = bytes.buffer;
      }
      this.ws.send(data);
      return true;
    } catch (err) {
      this.emit("error", err as Error);
      return false;
    }
  }

  /**
   * 获取当前连接状态
   * @returns 当前是否已连接
   */
  public isConnected(): boolean {
    return this.connected;
  }

  /**
   * 销毁实例，完全清理所有定时器和事件监听
   */
  public destroy(): void {
    this.disconnect(true);
    // 清空所有事件回调，避免内存泄漏
    this.eventHandlers = {
      open: [],
      close: [],
      message: [],
      error: [],
      reconnect: [],
    };
  }
}
